import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * The variant matrix — the axis grid, and the one cell it is made of.
 *
 * **Mostly empty on purpose.** An open cell is an *invitation*, never a hole and never a
 * missing record: nothing is stored for one, and the absence itself is the state. That is
 * the whole point of resolve-and-cache, expressed as a control (PRD 04 §4.3).
 *
 * It is a React Aria `Table` — the only RAC collection with genuine two-dimensional keyboard
 * navigation, and the one that gives real `columnheader` / `rowheader` semantics, so a
 * reader hears "sitting, calm" from the headers rather than from forty repeated cell labels.
 * `useGrid` is not exported from `react-aria`, and a `GridList` in grid layout derives its
 * columns from measured geometry and has no headers at all; a table is what this actually is.
 *
 * The `<table>` is `display: grid` and its row groups and rows are `display: contents`, so
 * the sheet's `grid-template-columns` still draws the matrix. RAC writes explicit roles on
 * every element, which is what makes that safe.
 */
import { Button as AriaButton, Cell, Column, Row, Table, TableBody, TableHeader, } from 'react-aria-components';
/**
 * The cell's whole look, by state — exported because the composer's variant swatch is this
 * cell at rail size (`studio/13`). Deriving it there rather than copying it is what keeps
 * one open cell in the product: change the invitation here and both places change.
 */
export const MATRIX_CELL_CLASS = {
    filled: 'mcell mcell-filled',
    open: 'mcell mcell-open',
    making: 'mcell mcell-making',
    error: 'mcell mcell-err',
};
export function MatrixCell({ state, label, children, verb, hint, disabled = false, onClick, }) {
    const body = state === 'filled' ? (children) : (_jsxs("span", { className: "verb", children: [verb, hint !== undefined && _jsx("i", { children: hint })] }));
    // A cell with nothing to do is not a button. "Making" is a state, not an action — so the
    // `<td>` itself carries the box and takes the focus ring.
    if (state === 'making' || onClick === undefined) {
        return (_jsx(Cell, { className: MATRIX_CELL_CLASS[state], textValue: label, "aria-label": label, children: body }));
    }
    // An actionable cell puts the button in the grid — `display: contents` on the `<td>` — so
    // the whole `.mcell` sheet applies to it and RAC's grid focuses it directly.
    return (_jsx(Cell, { className: "mcell-slot", textValue: label, children: _jsx(AriaButton, { className: MATRIX_CELL_CLASS[state], "aria-label": label, isDisabled: disabled, onPress: onClick, children: body }) }));
}
/**
 * The grid. Arrow keys move between cells in both directions, Home/End jump along a row and
 * ⌘/Ctrl+Home/End to the corners — all RAC's. The headers are real `columnheader` /
 * `rowheader`, so a screen reader announces the point without the cell repeating it.
 *
 * A one-axis matrix is still a table with one row: its row header carries the axis name and
 * is visually hidden, which keeps the grid template — and so the pixels — exactly as drawn.
 */
export function Matrix({ label, columns, rows, children }) {
    const hasRows = rows.length > 0;
    const columnCount = Math.max(1, columns.length);
    return (_jsx("div", { className: "matrixwrap", children: _jsxs(Table, { className: "matrix", "aria-label": label, style: {
                gridTemplateColumns: hasRows
                    ? `104px repeat(${columnCount}, 104px)`
                    : `repeat(${columnCount}, 104px)`,
            }, children: [_jsxs(TableHeader, { className: "mgroup", children: [_jsx(Column, { id: "axis", isRowHeader: true, className: hasRows ? 'mh mh-corner' : 'vh', children: _jsx("span", { className: hasRows ? 'vh' : undefined, children: label }) }), columns.map((column, index) => (_jsx(Column, { id: `c${index}`, className: "mh", children: column }, index)))] }), _jsx(TableBody, { className: "mgroup", children: hasRows
                        ? rows.map((row, rowIndex) => (_jsxs(Row, { id: `r${rowIndex}`, className: "mrow", children: [_jsx(Cell, { className: "mh mh-row", children: row }), children.slice(rowIndex * columnCount, (rowIndex + 1) * columnCount)] }, rowIndex)))
                        : [
                            _jsxs(Row, { id: "r0", className: "mrow", children: [_jsx(Cell, { className: "vh", children: label }), children] }, "r0"),
                        ] })] }) }));
}
//# sourceMappingURL=Matrix.js.map